import React, { useEffect, useMemo, useState } from "react";
import { useTable, useFilters, useSortBy } from "react-table";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "pages/components/PageHeader";
import createAutocompleteFilter from "pages/utils/autocompleteFilter";

import Draggable from "react-draggable";
import Paper from "@mui/material/Paper";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import {
  Autocomplete,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

function PaperComponent(props) {
  return (
    <Draggable
      handle=".draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"], input, textarea, select, button'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

function TextFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="filtrar..."
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
    />
  );
}

const toRef = (x) => (x?.ref ?? x?.Ref ?? "").toString();
const toNome = (x) => (x?.nome ?? x?.Nome ?? "").toString();

const autocompleteFilter = createAutocompleteFilter();

const dateOnly = (v) => {
  if (!v) return "";
  return String(v).split("T")[0];
};

const emptyForm = {
  id: 0,
  vendedorOrigem: null,
  vendedorDestino: null,
  dataini: "",
  datafim: "",
  obs: "",
  grupos: [],
};

export default function VendedoresSubstituicoes() {
  const { showAlert } = useAlert();
  const { temPermissao } = useAuth();

  const podeCriar = temPermissao("Menu Administração", "w");
  const podeEditar = temPermissao("Menu Administração", "u");
  const podeApagar = temPermissao("Menu Administração", "d");

  const TABLE_KEY = "vendedores-substituicoes";
  const LS_FILTERS_KEY = `tbl:${TABLE_KEY}:filters`;
  const LS_SORT_KEY = `tbl:${TABLE_KEY}:sortBy`;
  const LS_KEEP_KEY = `tbl:${TABLE_KEY}:keep`;

  const [keepFilters, setKeepFilters] = useState(() => localStorage.getItem(LS_KEEP_KEY) !== "0");

  const [loading, setLoading] = useState(false);
  const [rowsData, setRowsData] = useState([]);

  const [vendedores, setVendedores] = useState([]);
  const [gruposOptions, setGruposOptions] = useState([]);

  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const [openDel, setOpenDel] = useState(false);
  const [targetDel, setTargetDel] = useState(null);
  const [saving, setSaving] = useState(false);

  const editando = Number(form?.id || 0) > 0;

  const vendedoresOpt = useMemo(
    () =>
      vendedores.map((x) => ({
        value: toRef(x),
        label: `${toNome(x)} (${toRef(x)})`,
        raw: x,
      })),
    [vendedores]
  );

  const gruposOpt = useMemo(
    () =>
      gruposOptions.map((x) => ({
        value: toRef(x),
        label: `${toNome(x)} (${toRef(x)})`,
        raw: x,
      })),
    [gruposOptions]
  );

  const carregar = async () => {
    setLoading(true);

    try {
      const [payload, err] = await handleApi(api.post("Vendedores/SelVendedoresSubstituicoes", {}));

      if (err) {
        showAlert(err, "error");
        setRowsData([]);
        return;
      }

      setRowsData(Array.isArray(payload) ? payload : []);
    } finally {
      setLoading(false);
    }
  };

  const carregarCombos = async () => {
    setLoading(true);

    try {
      const [vends, errVends] = await handleApi(api.post("Vendedores/SelVendedoresCombo", {}));
      if (errVends) showAlert(errVends, "error");
      else setVendedores(Array.isArray(vends) ? vends : []);

      const [grps, errGrps] = await handleApi(api.post("Vendedores/SelMateriaisGruposCombo", {}));
      if (errGrps) showAlert(errGrps, "error");
      else setGruposOptions(Array.isArray(grps) ? grps : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarCombos();
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const abrirNovo = () => {
    setForm(emptyForm);
    setOpenDialog(true);
  };

  const abrirEditar = async (row) => {
    if (!row?.id) return;

    setLoading(true);

    try {
      const [payload, err] = await handleApi(
        api.post("Vendedores/SelVendedorSubstituicao", { id: row.id })
      );

      if (err) {
        showAlert(err, "error");
        return;
      }

      const s = payload?.substituicao || {};
      const grupos = Array.isArray(payload?.grupos) ? payload.grupos : [];

      const origem =
        vendedoresOpt.find((x) => String(x.value) === String(s.refvendedororigem)) || null;
      const destino =
        vendedoresOpt.find((x) => String(x.value) === String(s.refvendedordestino)) || null;

      setForm({
        id: Number(s.id || 0),
        vendedorOrigem: origem,
        vendedorDestino: destino,
        dataini: dateOnly(s.dataini),
        datafim: dateOnly(s.datafim),
        obs: s.obs || "",
        grupos,
      });

      setOpenDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const toggleGrupo = (ref) => {
    setForm((prev) => {
      const atual = Array.isArray(prev.grupos) ? prev.grupos : [];
      const existe = atual.includes(ref);

      return {
        ...prev,
        grupos: existe ? atual.filter((x) => x !== ref) : [...atual, ref],
      };
    });
  };

  const selecionarTodosGrupos = () => {
    setForm((prev) => ({
      ...prev,
      grupos: gruposOpt.map((x) => x.value),
    }));
  };

  const desselecionarTodosGrupos = () => {
    setForm((prev) => ({
      ...prev,
      grupos: [],
    }));
  };

  const guardar = async () => {
    const refOrigem = form.vendedorOrigem?.value || "";
    const refDestino = form.vendedorDestino?.value || "";

    if (!refOrigem) return showAlert("Seleciona o vendedor origem.", "warning");
    if (!refDestino) return showAlert("Seleciona o vendedor destino.", "warning");
    if (refOrigem === refDestino)
      return showAlert("O vendedor origem e destino não podem ser iguais.", "warning");
    if (!form.dataini) return showAlert("Preenche a data inicial.", "warning");
    if (!Array.isArray(form.grupos) || form.grupos.length === 0)
      return showAlert("Seleciona pelo menos um grupo de materiais.", "warning");

    const body = {
      id: Number(form.id || 0),
      refvendedororigem: refOrigem,
      refvendedordestino: refDestino,
      dataini: form.dataini,
      datafim: form.datafim || null,
      obs: form.obs || "",
      grupos: form.grupos,
    };

    const endpoint = editando
      ? "Vendedores/UpdVendedorSubstituicao"
      : "Vendedores/InsVendedorSubstituicao";

    setSaving(true);

    try {
      const [_payload, err, meta] = await handleApi(api.post(endpoint, body));

      if (err) {
        showAlert(err, "error");
        return;
      }

      showAlert(
        meta?.message || (editando ? "Substituição atualizada." : "Substituição criada."),
        meta?.severity || "success"
      );

      setOpenDialog(false);
      setForm(emptyForm);
      await carregar();
    } finally {
      setSaving(false);
    }
  };

  const pedirApagar = (row) => {
    setTargetDel(row);
    setOpenDel(true);
  };

  const confirmarApagar = async () => {
    if (!targetDel?.id) return;

    setSaving(true);

    try {
      const [_payload, err, meta] = await handleApi(
        api.post("Vendedores/DelVendedorSubstituicao", { id: targetDel.id })
      );

      if (err) {
        showAlert(err, "error");
        return;
      }

      showAlert(meta?.message || "Substituição apagada.", meta?.severity || "success");

      setOpenDel(false);
      setTargetDel(null);
      await carregar();
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "",
        id: "acoes",
        disableFilters: true,
        width: 72,
        minWidth: 56,
        maxWidth: 90,
        Cell: ({ row }) => {
          const r = row.original;

          return (
            <div style={{ display: "flex", gap: 4, whiteSpace: "nowrap" }}>
              <Tooltip title="Editar">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={!podeEditar}
                    onClick={() => abrirEditar(r)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Apagar">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={!podeApagar}
                    onClick={() => pedirApagar(r)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          );
        },
      },
      { Header: "Origem", accessor: "vendedororigem", Filter: TextFilter },
      { Header: "Ref. Origem", accessor: "refvendedororigem", Filter: TextFilter },
      { Header: "Destino", accessor: "vendedordestino", Filter: TextFilter },
      { Header: "Ref. Destino", accessor: "refvendedordestino", Filter: TextFilter },
      {
        Header: "Data Inicial",
        accessor: "dataini",
        Filter: TextFilter,
        Cell: ({ value }) => dateOnly(value),
      },
      {
        Header: "Data Final",
        accessor: "datafim",
        Filter: TextFilter,
        Cell: ({ value }) => dateOnly(value),
      },
      { Header: "Grupos", accessor: "grupos_nomes", Filter: TextFilter },
      { Header: "Observações", accessor: "obs", Filter: TextFilter },
    ],
    [podeEditar, podeApagar, vendedoresOpt]
  );

  const savedFilters = useMemo(() => {
    try {
      const raw = localStorage.getItem(LS_FILTERS_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, []);

  const savedSortBy = useMemo(() => {
    try {
      const raw = localStorage.getItem(LS_SORT_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, []);

  const table = useTable(
    {
      columns,
      data: rowsData,
      initialState: {
        filters: savedFilters,
        sortBy: savedSortBy,
      },
      autoResetFilters: false,
      autoResetSortBy: false,
    },
    useFilters,
    useSortBy
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: tableState,
    setAllFilters,
    setSortBy,
  } = table;

  useEffect(() => {
    if (!keepFilters) return;

    try {
      localStorage.setItem(LS_FILTERS_KEY, JSON.stringify(tableState.filters || []));
      localStorage.setItem(LS_SORT_KEY, JSON.stringify(tableState.sortBy || []));
      localStorage.setItem(LS_KEEP_KEY, "1");
    } catch {}
  }, [tableState.filters, tableState.sortBy, keepFilters]);

  useEffect(() => {
    if (!keepFilters) {
      try {
        localStorage.removeItem(LS_FILTERS_KEY);
        localStorage.removeItem(LS_SORT_KEY);
        localStorage.setItem(LS_KEEP_KEY, "0");
      } catch {}
    }
  }, [keepFilters]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {loading && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}

      <PageHeader />

      <MDBox mt={2}>
        <Card>
          <MDBox
            p={2}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={keepFilters}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setKeepFilters(checked);

                    if (!checked) {
                      setAllFilters([]);
                      setSortBy([]);
                    }
                  }}
                  size="small"
                />
              }
              label="Manter filtros"
            />

            <MDButton color="success" onClick={abrirNovo} disabled={!podeCriar}>
              Nova Substituição
            </MDButton>
          </MDBox>

          <MDBox p={2} pt={0}>
            <div style={{ width: "100%", overflowX: "auto" }}>
              <table
                {...getTableProps()}
                style={{
                  width: "100%",
                  minWidth: 1100,
                  borderCollapse: "collapse",
                }}
              >
                <thead>
                  {headerGroups.map((hg) => (
                    <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                      {hg.headers.map((col) => (
                        <th
                          {...col.getHeaderProps(col.getSortByToggleProps())}
                          key={col.id}
                          style={{
                            textAlign: "left",
                            padding: "10px 8px",
                            borderBottom: "1px solid rgba(0,0,0,0.08)",
                            fontSize: 12,
                            color: "#344767",
                            whiteSpace: "nowrap",
                            width: col.id === "acoes" ? 72 : undefined,
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span>{col.render("Header")}</span>
                            <span style={{ fontSize: 11, opacity: 0.7 }}>
                              {col.isSorted ? (col.isSortedDesc ? "▼" : "▲") : ""}
                            </span>
                          </div>

                          {!col.disableFilters && (
                            <div style={{ marginTop: 6 }}>{col.render("Filter")}</div>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>

                <tbody {...getTableBodyProps()}>
                  {rows.map((row) => {
                    prepareRow(row);

                    return (
                      <tr
                        {...row.getRowProps()}
                        key={row.id}
                        style={{ borderBottom: "1px solid rgba(0,0,0,0.05)" }}
                      >
                        {row.cells.map((cell) => (
                          <td
                            {...cell.getCellProps()}
                            key={cell.column.id}
                            style={{
                              padding: "10px 8px",
                              fontSize: 13,
                              color: "#344767",
                              whiteSpace: "nowrap",
                              verticalAlign: "middle",
                              width: cell.column.id === "acoes" ? 72 : undefined,
                            }}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}

                  {rows.length === 0 && !loading && (
                    <tr>
                      <td colSpan={columns.length} style={{ padding: 16, color: "#67748e" }}>
                        Sem substituições registadas.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </MDBox>
        </Card>
      </MDBox>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperComponent={PaperComponent}
      >
        <DialogTitle className="draggable-dialog-title" style={{ cursor: "move" }}>
          {editando ? "Editar Substituição" : "Nova Substituição"}
        </DialogTitle>

        <DialogContent dividers>
          <MDBox display="flex" flexWrap="wrap" gap={2}>
            <MDBox sx={{ flex: "1 1 320px" }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5, display: "block" }}>
                Vendedor origem
              </MDTypography>

              <Autocomplete
                options={vendedoresOpt}
                value={form.vendedorOrigem}
                filterOptions={autocompleteFilter}
                onChange={(_e, v) => setForm((prev) => ({ ...prev, vendedorOrigem: v }))}
                getOptionLabel={(o) => o?.label || ""}
                isOptionEqualToValue={(a, b) => a?.value === b?.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Selecionar vendedor origem..."
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
                  />
                )}
              />
            </MDBox>

            <MDBox sx={{ flex: "1 1 320px" }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5, display: "block" }}>
                Vendedor destino
              </MDTypography>

              <Autocomplete
                options={vendedoresOpt}
                value={form.vendedorDestino}
                filterOptions={autocompleteFilter}
                onChange={(_e, v) => setForm((prev) => ({ ...prev, vendedorDestino: v }))}
                getOptionLabel={(o) => o?.label || ""}
                isOptionEqualToValue={(a, b) => a?.value === b?.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Selecionar vendedor destino..."
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
                  />
                )}
              />
            </MDBox>

            <MDBox sx={{ flex: "1 1 180px" }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5, display: "block" }}>
                Data inicial
              </MDTypography>

              <TextField
                type="date"
                fullWidth
                value={form.dataini}
                onChange={(e) => setForm((prev) => ({ ...prev, dataini: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
              />
            </MDBox>

            <MDBox sx={{ flex: "1 1 180px" }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5, display: "block" }}>
                Data final
              </MDTypography>

              <TextField
                type="date"
                fullWidth
                value={form.datafim}
                onChange={(e) => setForm((prev) => ({ ...prev, datafim: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
              />
            </MDBox>

            <MDBox sx={{ flex: "1 1 100%" }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5, display: "block" }}>
                Observações
              </MDTypography>

              <TextField
                fullWidth
                multiline
                minRows={2}
                value={form.obs}
                onChange={(e) => setForm((prev) => ({ ...prev, obs: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
              />
            </MDBox>

            <MDBox sx={{ flex: "1 1 100%" }}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <MDTypography variant="button" sx={{ fontSize: 13 }}>
                    Grupos de materiais ({form.grupos.length})
                  </MDTypography>
                </AccordionSummary>

                <AccordionDetails>
                  <MDBox display="flex" flexWrap="wrap" gap={1} mb={2}>
                    <MDButton
                      color="info"
                      variant="outlined"
                      size="small"
                      onClick={selecionarTodosGrupos}
                    >
                      Selecionar todos
                    </MDButton>

                    <MDButton
                      color="secondary"
                      variant="outlined"
                      size="small"
                      onClick={desselecionarTodosGrupos}
                    >
                      Desselecionar todos
                    </MDButton>
                  </MDBox>

                  <MDBox
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(260px, 1fr))",
                        md: "repeat(3, minmax(260px, 1fr))",
                      },
                      columnGap: 3,
                      rowGap: 1.5,
                      pl: 2,
                      pr: 2,
                      maxHeight: 280,
                      overflow: "auto",
                    }}
                  >
                    {gruposOpt.map((g) => (
                      <MDBox
                        key={g.value}
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{ minWidth: 0 }}
                      >
                        <Checkbox
                          size="small"
                          checked={form.grupos.includes(g.value)}
                          onChange={() => toggleGrupo(g.value)}
                          sx={{ flexShrink: 0 }}
                        />

                        <MDTypography
                          variant="button"
                          sx={{
                            fontSize: 12,
                            lineHeight: 1.25,
                            color: "#344767",
                            whiteSpace: "normal",
                            wordBreak: "normal",
                          }}
                        >
                          {g.label}
                        </MDTypography>
                      </MDBox>
                    ))}
                  </MDBox>
                </AccordionDetails>
              </Accordion>
            </MDBox>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton color="secondary" onClick={() => setOpenDialog(false)} disabled={saving}>
            Cancelar
          </MDButton>

          <MDButton color={editando ? "info" : "success"} onClick={guardar} disabled={saving}>
            {saving ? "A gravar..." : editando ? "Atualizar" : "Gravar"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDel}
        onClose={() => setOpenDel(false)}
        maxWidth="xs"
        fullWidth
        PaperComponent={PaperComponent}
      >
        <DialogTitle className="draggable-dialog-title" style={{ cursor: "move" }}>
          Confirmar eliminação
        </DialogTitle>

        <DialogContent dividers>
          <MDTypography variant="body2">
            Tens a certeza que queres apagar esta substituição?
          </MDTypography>

          <MDBox mt={1}>
            <MDTypography variant="button" sx={{ fontSize: 12 }}>
              <strong>{targetDel?.vendedororigem}</strong> →{" "}
              <strong>{targetDel?.vendedordestino}</strong>
            </MDTypography>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton color="secondary" onClick={() => setOpenDel(false)} disabled={saving}>
            Cancelar
          </MDButton>

          <MDButton color="error" onClick={confirmarApagar} disabled={saving}>
            {saving ? "A apagar..." : "Apagar"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
